import { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useFamily } from '../../contexts/FamilyContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';

const IDENTITY_OPTIONS = [
  { value: 'secular', label: 'Secular' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'reform', label: 'Reform' },
  { value: 'conservative', label: 'Conservative' },
  { value: 'reconstructionist', label: 'Reconstructionist' },
  { value: 'other', label: 'Other' },
];

const STARTING_POINT_OPTIONS = [
  { value: 'just-beginning', label: 'Just Beginning' },
  { value: 'holidays-only', label: 'Holidays Only' },
  { value: 'dabbling', label: 'Dabbling' },
  { value: 'pretty-active', label: 'Pretty Active' },
];

const GOALS_OPTIONS = [
  { value: 'teach-kids', label: 'Teach Kids About Judaism' },
  { value: 'create-traditions', label: 'Create Family Traditions' },
  { value: 'connect-community', label: 'Connect with Community' },
  { value: 'celebrate-holidays', label: 'Celebrate Holidays Meaningfully' },
  { value: 'explore-identity', label: 'Explore Jewish Identity' },
];

const PARENTING_PRIORITIES = [
  { value: 'fun', label: 'Make it Fun' },
  { value: 'educational', label: 'Educational' },
  { value: 'meaningful', label: 'Meaningful' },
  { value: 'flexible', label: 'Flexible' },
  { value: 'inclusive', label: 'Inclusive' },
];

const TIME_COMMITMENT_OPTIONS = [
  { value: 'minimal', label: 'Minimal (15-30 min/week)' },
  { value: 'moderate', label: 'Moderate (1-2 hours/week)' },
  { value: 'active', label: 'Active (3+ hours/week)' },
];

export default function OnboardingSettings() {
  const { family, familyId } = useFamily();
  const [loading, setLoading] = useState(false);
  const [onboardingData, setOnboardingData] = useState({
    identity: '',
    startingPoint: '',
    goals: [],
    parentingPriorities: [],
    timeCommitment: '',
  });

  useEffect(() => {
    if (family?.onboarding) {
      setOnboardingData({
        identity: family.onboarding.identity || '',
        startingPoint: family.onboarding.startingPoint || '',
        goals: family.onboarding.goals || [],
        parentingPriorities: family.onboarding.parentingPriorities || [],
        timeCommitment: family.onboarding.timeCommitment || '',
      });
    }
  }, [family]);

  const handleToggleArray = (array, value) => {
    if (array.includes(value)) {
      return array.filter(item => item !== value);
    }
    return [...array, value];
  };

  const handleSave = async () => {
    if (!familyId) return;

    setLoading(true);
    try {
      await updateDoc(doc(db, 'families', familyId), {
        onboarding: {
          ...family.onboarding,
          ...onboardingData,
        },
        updatedAt: new Date(),
      });

      toast.success('Onboarding settings updated!');
    } catch (error) {
      console.error('Error updating onboarding:', error);
      toast.error('Failed to update onboarding settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-card shadow-card border border-border p-6 sm:p-8">
      <h2 className="text-xl sm:text-2xl font-semibold text-text-primary mb-2">Onboarding Data</h2>
      <p className="text-sm text-text-secondary mb-6">Edit your onboarding responses to update personalization</p>

      {/* Jewish Identity */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-3">
          Jewish Identity
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {IDENTITY_OPTIONS.map((option) => (
            <label key={option.value} className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-surface transition-colors">
              <input
                type="radio"
                name="identity"
                value={option.value}
                checked={onboardingData.identity === option.value}
                onChange={(e) => setOnboardingData({ ...onboardingData, identity: e.target.value })}
                className="w-4 h-4 text-primary"
              />
              <span className="text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Starting Point */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-3">
          Starting Point
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {STARTING_POINT_OPTIONS.map((option) => (
            <label key={option.value} className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-surface transition-colors">
              <input
                type="radio"
                name="startingPoint"
                value={option.value}
                checked={onboardingData.startingPoint === option.value}
                onChange={(e) => setOnboardingData({ ...onboardingData, startingPoint: e.target.value })}
                className="w-4 h-4 text-primary"
              />
              <span className="text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Goals */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-3">
          Goals (Select all that apply)
        </label>
        <div className="space-y-2">
          {GOALS_OPTIONS.map((option) => (
            <label key={option.value} className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-surface transition-colors">
              <input
                type="checkbox"
                checked={onboardingData.goals.includes(option.value)}
                onChange={() => setOnboardingData({
                  ...onboardingData,
                  goals: handleToggleArray(onboardingData.goals, option.value)
                })}
                className="w-4 h-4 text-primary rounded"
              />
              <span className="text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Parenting Priorities */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-3">
          Parenting Priorities (Select all that apply)
        </label>
        <div className="space-y-2">
          {PARENTING_PRIORITIES.map((option) => (
            <label key={option.value} className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-surface transition-colors">
              <input
                type="checkbox"
                checked={onboardingData.parentingPriorities.includes(option.value)}
                onChange={() => setOnboardingData({
                  ...onboardingData,
                  parentingPriorities: handleToggleArray(onboardingData.parentingPriorities, option.value)
                })}
                className="w-4 h-4 text-primary rounded"
              />
              <span className="text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Time Commitment */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-3">
          Time Commitment
        </label>
        <div className="space-y-2">
          {TIME_COMMITMENT_OPTIONS.map((option) => (
            <label key={option.value} className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-surface transition-colors">
              <input
                type="radio"
                name="timeCommitment"
                value={option.value}
                checked={onboardingData.timeCommitment === option.value}
                onChange={(e) => setOnboardingData({ ...onboardingData, timeCommitment: e.target.value })}
                className="w-4 h-4 text-primary"
              />
              <span className="text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={loading}
        className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FontAwesomeIcon icon={faSave} />
        <span>{loading ? 'Saving...' : 'Save Changes'}</span>
      </button>
    </div>
  );
}

